import {
    OfflineSettlementPolicy,
    getOfflineSettlementPolicy,
} from '@/lib/api/financial/financial-settings-api'
import { useEffect, useState } from 'react'

/**
 * Cache de sessão da política.
 *
 * O `BillingInfoSection` aparece no detalhe de exame, consulta, procedimento,
 * teleconsulta e pedido — sem isto, cada uma dessas telas dispararia a mesma
 * requisição. A política muda raramente (é uma configuração administrativa) e um
 * refresh da página já a renova.
 */
let cached: OfflineSettlementPolicy | null = null
let inFlight: Promise<OfflineSettlementPolicy | null> | null = null

function fetchPolicy(): Promise<OfflineSettlementPolicy | null> {
    if (cached) return Promise.resolve(cached)
    if (inFlight) return inFlight

    inFlight = getOfflineSettlementPolicy()
        .then(res => {
            // Sem permissão ou erro: assume desligado (comportamento antigo,
            // com comprovante e fila). Errar para o lado restritivo aqui só
            // mostra um botão desabilitado; o contrário liberaria uma baixa que
            // o backend recusaria depois.
            if (!res.success || !res.result) return null
            cached = res.result
            return cached
        })
        .catch(() => null)
        .finally(() => {
            inFlight = null
        })

    return inFlight
}

/** Invalida o cache após salvar a política (tela de configurações). */
export function resetOfflineSettlementPolicyCache(): void {
    cached = null
}

/**
 * Diz se as cobranças recebidas por fora do sistema (dinheiro, maquininha,
 * outro) recebem baixa direta, sem comprovante e sem fila de aprovação.
 */
export function useOfflineSettlementPolicy(): { autoSettle: boolean } {
    const [autoSettle, setAutoSettle] = useState(cached?.autoSettle ?? false)

    useEffect(() => {
        let cancelled = false
        fetchPolicy().then(policy => {
            if (!cancelled && policy) setAutoSettle(policy.autoSettle)
        })
        return () => {
            cancelled = true
        }
    }, [])

    return { autoSettle }
}
